import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Tooltip, message, Typography, Card, Modal, Alert, Form, Input } from 'antd';
import { ExportOutlined, PlayCircleOutlined, CheckCircleOutlined, AuditOutlined, FileTextOutlined, FileExcelOutlined, DeleteOutlined } from '@ant-design/icons';
import { ThunderboltOutlined } from '@ant-design/icons';
import WorkRecordActions from '../assetWorkRecord/WorkRecordActions';
import apiClient from '../../services/api';
import { formatVND } from '../../utils/formatters';

const { Title, Text } = Typography;

interface WorkRecord {
  id: number;
  asset_code: string;
  asset_name: string;
  maintenance_type_name: string;
  work_description?: string;
  status: string;
  work_date: string;
  completed_date?: string;
  cost?: number;
  notes?: string;
  company_id?: number;
  asset_id?: number;
  work_documents?: string;
  employee_name?: string;
  supplier_name?: string;
}

interface CampaignWorkRecordsTableProps {
  campaignId: number;
  campaignName: string;
  onRefresh: () => void;
  campaignStatus?: string;
  onApproveExecution?: () => void;
  onApproveCompletion?: () => void;
  companyId?: number;
}

const CampaignWorkRecordsTable: React.FC<CampaignWorkRecordsTableProps> = ({
  campaignId,
  campaignName,
  onRefresh,
  campaignStatus,
  onApproveExecution,
  onApproveCompletion,
  companyId
}) => {
  const [workRecords, setWorkRecords] = useState<WorkRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [completionModalVisible, setCompletionModalVisible] = useState(false);
  const [completionDocumentCode, setCompletionDocumentCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Thêm state cho select box
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // State cho hoàn thành nhanh hàng loạt
  const [bulkQuickModalVisible, setBulkQuickModalVisible] = useState(false);
  const [bulkQuickLoading, setBulkQuickLoading] = useState(false);
  const [bulkQuickForm] = Form.useForm();
  // Thêm state cho pagination
  const [pageSize, setPageSize] = useState(10);

  const fetchWorkRecords = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getMaintenanceCampaignWorkRecords(campaignId);
      setWorkRecords(Array.isArray(response) ? response : []);
    } catch (error: any) {
      message.error('Không thể tải danh sách tác nghiệp');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkRecords();
  }, [campaignId]);

  const handleActionSuccess = (actionType: string) => {
    // Hiển thị message tùy theo loại action
    switch (actionType) {
      case 'complete':
        message.success('Đã hoàn thành tác nghiệp thành công!');
        break;
      case 'quick_complete':
        message.success('Đã hoàn thành tác nghiệp nhanh!');
        break;
      case 'delete':
        message.success('Đã xóa tác nghiệp thành công!');
        break;
      default:
        break;
    }
    
    fetchWorkRecords(); // Chỉ reload bảng tác nghiệp
    // Không gọi onRefresh() để tránh reload toàn trang
  };

  // Kiểm tra xem tất cả tác nghiệp đã hoàn thành chưa
  const allWorkRecordsCompleted = workRecords.length > 0 && 
    workRecords.every(record => record.status === 'completed');

  const handleExportExcel = () => {
    // TODO: Implement export to Excel
    message.info('Tính năng xuất Excel đang phát triển');
  };

  const handleCompleteCampaign = async () => {
    if (!completionDocumentCode.trim()) {
      message.error('Vui lòng nhập mã hồ sơ hoàn thành');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.approveMaintenanceCampaignCompletion(campaignId, {
        completion_document_code: completionDocumentCode
      });

      message.success('Đã hoàn thành đợt tác nghiệp thành công!');
      setCompletionModalVisible(false);
      setCompletionDocumentCode('');
      onRefresh();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || 'Không thể hoàn thành đợt tác nghiệp';
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Xử lý chọn hàng
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  // Cấu hình select box
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: WorkRecord) => ({
      // Chỉ cho phép chọn những tác nghiệp có thể xóa
      disabled: !['pending_execution_approval', 'execution_approved', 'in_progress'].includes(record.status),
    }),
  };

  // Xử lý xóa hàng loạt
  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một tác nghiệp để xóa');
      return;
    }
    setDeleteModalVisible(true);
  };

  const handleConfirmBulkDelete = async () => {
    setDeleting(true);
    try {
      // Xóa từng tác nghiệp một (vì chưa có API xóa hàng loạt)
      const deletePromises = selectedRowKeys.map(id => 
        apiClient.deleteAssetWorkRecord(Number(id))
      );
      
      await Promise.all(deletePromises);
      
      message.success(`Đã xóa thành công ${selectedRowKeys.length} tác nghiệp`);
      setDeleteModalVisible(false);
      setSelectedRowKeys([]);
      fetchWorkRecords();
    } catch (error: any) {
      message.error('Có lỗi xảy ra khi xóa tác nghiệp');
      console.error('Bulk delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      title: 'Mã tài sản',
      dataIndex: 'asset_code',
      key: 'asset_code',
      width: 120,
      render: (text: string, record: WorkRecord) => (
        <a onClick={() => {
          // TODO: Navigate to asset detail
          message.info('Tính năng xem chi tiết tài sản đang phát triển');
        }}>
          {text}
        </a>
      ),
    },
    {
      title: 'Tên tài sản',
      dataIndex: 'asset_name',
      key: 'asset_name',
      width: 150,
    },
    {
      title: 'Loại tác nghiệp',
      dataIndex: 'maintenance_type_name',
      key: 'maintenance_type_name',
      width: 180,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => {
        const getStatusColor = (status: string) => {
          switch (status) {
            case 'pending_execution_approval':
              return 'orange';
            case 'execution_approved':
              return 'blue';
            case 'in_progress':
              return 'processing';
            case 'pending_completion_approval':
              return 'purple';
            case 'completed':
              return 'success';
            case 'skipped':
              return 'error';
            case 'failed':
              return 'error';
            default:
              return 'default';
          }
        };

        const getStatusText = (status: string) => {
          switch (status) {
            case 'pending_execution_approval':
              return 'Chờ duyệt thực hiện';
            case 'execution_approved':
              return 'Đang thực hiện';
            case 'in_progress':
              return 'Đang thực hiện';
            case 'pending_completion_approval':
              return 'Chờ duyệt hoàn thành';
            case 'completed':
              return 'Hoàn thành';
            case 'skipped':
              return 'Bỏ qua';
            case 'failed':
              return 'Thất bại';
            default:
              return status;
          }
        };

        return (
          <Tag color={getStatusColor(status)}>
            {getStatusText(status)}
          </Tag>
        );
      },
    },
    {
      title: 'Ngày hoàn thành',
      dataIndex: 'completed_date',
      key: 'completed_date',
      width: 120,
      render: (date: string) => date ? new Date(date).toLocaleDateString('vi-VN') : '—',
    },
    {
      title: 'Chi phí',
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      render: (cost: number) => cost ? formatVND(cost) : '—',
    },
    {
      title: 'Mã hồ sơ',
      dataIndex: 'work_documents',
      key: 'work_documents',
      width: 120,
      render: (work_documents: string) => work_documents ? (
        <Tag color="blue" icon={<FileTextOutlined />}>
          {work_documents}
        </Tag>
      ) : '—',
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (text: string, record: WorkRecord) => (
        <WorkRecordActions
          workRecord={record}
          onActionSuccess={handleActionSuccess}
          companyId={companyId}
        />
      ),
    },
  ];

  // Tính toán khả năng hoàn thành nhanh hàng loạt
  const selectedRecords = selectedRowKeys
    .map(id => workRecords.find(r => r.id === id))
    .filter((r): r is WorkRecord => Boolean(r));
  const canBulkQuickComplete = selectedRecords.length > 0 && selectedRecords.every(r => ['in_progress', 'execution_approved'].includes(r.status));

  const handleBulkQuickCompleteSubmit = async (values: any) => {
    setBulkQuickLoading(true);
    try {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const completed_date = `${yyyy}-${mm}-${dd}`;

      const promises = selectedRecords.map(rec =>
        apiClient.completeWorkRecord(rec.id, {
          completed_date,
          cost: 0,
          notes: 'Hoàn thành nhanh (hàng loạt)',
          work_documents: values.work_documents,
          performed_by_employee_id: null,
          performed_by_supplier_id: null,
        })
      );
      await Promise.all(promises);
      message.success(`Đã hoàn thành nhanh ${selectedRecords.length} tác nghiệp`);
      setBulkQuickModalVisible(false);
      bulkQuickForm.resetFields();
      setSelectedRowKeys([]);
      fetchWorkRecords();
    } catch (error: any) {
      console.error('Bulk quick complete error:', error);
      message.error(error?.response?.data?.message || 'Không thể hoàn thành nhanh hàng loạt');
    } finally {
      setBulkQuickLoading(false);
    }
  };

  return (
    <>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>Danh sách tác nghiệp</Title>
            <Text type="secondary">({workRecords.length} tác nghiệp)</Text>
            {selectedRowKeys.length > 0 && (
              <Text type="warning" strong>
                ({selectedRowKeys.length} đã chọn)
              </Text>
            )}
          </Space>
        }
        extra={
          <Space>
            {/* Nút xóa hàng loạt */}
            {selectedRowKeys.length > 0 && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDelete}
                disabled={deleting}
              >
                Xóa đã chọn ({selectedRowKeys.length})
              </Button>
            )}

            {/* Nút hoàn thành nhanh hàng loạt */}
            {selectedRowKeys.length > 0 && (
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => setBulkQuickModalVisible(true)}
                disabled={!canBulkQuickComplete}
                style={{ backgroundColor: canBulkQuickComplete ? '#52c41a' : undefined }}
              >
                Hoàn thành nhanh ({selectedRowKeys.length})
              </Button>
            )}
            
            {/* Nút duyệt thực hiện - chỉ hiện khi status = pending_execution_approval */}
            {campaignStatus === 'pending_execution_approval' && onApproveExecution && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={onApproveExecution}
              >
                Duyệt thực hiện
              </Button>
            )}
            
            {/* Nút duyệt hoàn thành - hiện tại vị trí nút duyệt thực hiện sau khi duyệt thực hiện */}
            {campaignStatus === 'in_progress' && onApproveCompletion && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={onApproveCompletion}
                disabled={!allWorkRecordsCompleted}
                style={{ 
                  backgroundColor: allWorkRecordsCompleted ? '#52c41a' : '#d9d9d9',
                  color: allWorkRecordsCompleted ? 'white' : '#666'
                }}
              >
                Duyệt hoàn thành
              </Button>
            )}
            
            <Button
              icon={<FileExcelOutlined />}
              onClick={async () => {
                try {
                  if (!campaignId) {
                    message.info('Không có campaign để xuất Excel');
                    return;
                  }
                  const token = localStorage.getItem('token');
                  const blob = await apiClient.exportMaintenanceCampaignExcel(Number(campaignId));
                  const link = document.createElement('a');
                  link.href = window.URL.createObjectURL(blob);
                  link.download = `work_records_campaign_${campaignId}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  message.success('Đã xuất Excel');
                } catch (err: any) {
                  console.error(err);
                  message.error(err?.message || 'Không thể xuất Excel');
                }
              }}
            >
              Xuất Excel
            </Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {/* Bảng tác nghiệp */}
        <Table
          columns={columns}
          dataSource={workRecords}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} tác nghiệp`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onShowSizeChange: (current, size) => setPageSize(size),
          }}
          scroll={{ x: 830 }}
          size="small"
        />
      </Card>

      {/* Modal xác nhận xóa hàng loạt */}
      <Modal
        title={
          <Space>
            <DeleteOutlined style={{ color: '#ff4d4f' }} />
            <span>Xác nhận xóa tác nghiệp</span>
          </Space>
        }
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <div style={{ marginBottom: 24 }}>
          <p>Bạn có chắc chắn muốn xóa <strong>{selectedRowKeys.length}</strong> tác nghiệp đã chọn không?</p>
          
          <div style={{
            background: '#f5f5f5',
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
            maxHeight: 200,
            overflowY: 'auto'
          }}>
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Danh sách tác nghiệp sẽ bị xóa:</div>
            {selectedRowKeys.map(id => {
              const record = workRecords.find(r => r.id === id);
              return record ? (
                <div key={id} style={{ marginBottom: 4, fontSize: '13px' }}>
                  • {record.asset_code} - {record.asset_name} ({record.maintenance_type_name})
                </div>
              ) : null;
            })}
          </div>
          
          <Alert
            message="Lưu ý quan trọng"
            description="Các tác nghiệp sẽ bị xóa vĩnh viễn và không thể khôi phục. Hãy đảm bảo rằng bạn thực sự muốn xóa những tác nghiệp này."
            type="warning"
            showIcon
          />
        </div>

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={() => setDeleteModalVisible(false)}>
              Hủy
            </Button>
            <Button
              type="primary"
              danger
              loading={deleting}
              icon={<DeleteOutlined />}
              onClick={handleConfirmBulkDelete}
            >
              Xóa {selectedRowKeys.length} tác nghiệp
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Modal hoàn thành campaign */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <span>Hoàn thành đợt tác nghiệp</span>
          </Space>
        }
        open={completionModalVisible}
        onCancel={() => {
          setCompletionModalVisible(false);
          setCompletionDocumentCode('');
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <div style={{ marginBottom: 24 }}>
          <Title level={5} style={{ marginBottom: 16 }}>Thông tin đợt tác nghiệp</Title>
          <div style={{ 
            background: '#f5f5f5', 
            padding: 16, 
            borderRadius: 8,
            marginBottom: 16
          }}>
            <div><strong>Tên đợt tác nghiệp:</strong> {campaignName}</div>
            <div><strong>ID:</strong> {campaignId}</div>
            <div><strong>Tiến độ hoàn thành:</strong> {Math.round((workRecords.filter(r => r.status === 'completed').length / workRecords.length) * 100)}%</div>
            <div><strong>Tổng tác nghiệp:</strong> {workRecords.length}</div>
            <div><strong>Đã hoàn thành:</strong> {workRecords.filter(r => r.status === 'completed').length}</div>
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <Alert
            message="Xác nhận hoàn thành"
            description="Bạn sắp hoàn thành đợt tác nghiệp này. Hãy đảm bảo rằng tất cả tác nghiệp đã được hoàn thành và kiểm tra kỹ lưỡng."
            type="info"
            showIcon
          />
        </div>

        <Form layout="vertical">
          <Form.Item
            label="Mã hồ sơ hoàn thành"
            required
            help="Mã hồ sơ này sẽ được lưu trữ để tham chiếu sau này"
          >
            <Input
              placeholder="Nhập mã hồ sơ hoàn thành"
              prefix={<FileTextOutlined />}
              value={completionDocumentCode}
              onChange={(e) => setCompletionDocumentCode(e.target.value)}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCompletionModalVisible(false);
                setCompletionDocumentCode('');
              }}>
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<CheckCircleOutlined />}
                onClick={handleCompleteCampaign}
                disabled={!completionDocumentCode.trim()}
              >
                Hoàn thành đợt tác nghiệp
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal hoàn thành nhanh hàng loạt */}
      <Modal
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#52c41a' }} />
            <span>Hoàn thành nhanh tác nghiệp (hàng loạt)</span>
          </Space>
        }
        open={bulkQuickModalVisible}
        onCancel={() => { setBulkQuickModalVisible(false); bulkQuickForm.resetFields(); }}
        footer={null}
        width={560}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            type={canBulkQuickComplete ? 'info' : 'warning'}
            showIcon
            message={canBulkQuickComplete ? 'Các tác nghiệp đủ điều kiện hoàn thành nhanh' : 'Trong danh sách chọn có tác nghiệp không đủ điều kiện hoàn thành nhanh'}
            description={
              <div>
                <div><strong>Số lượng đã chọn:</strong> {selectedRecords.length}</div>
                {!canBulkQuickComplete && (
                  <div>Chỉ có thể hoàn thành nhanh khi trạng thái là "Đang thực hiện" hoặc "Đã duyệt thực hiện".</div>
                )}
              </div>
            }
            style={{ marginBottom: 12 }}
          />
        </div>
        <Form form={bulkQuickForm} layout="vertical" onFinish={handleBulkQuickCompleteSubmit}>
          <Form.Item
            label="Mã hồ sơ tác nghiệp"
            name="work_documents"
            rules={[{ required: true, message: 'Vui lòng nhập mã hồ sơ tác nghiệp' }]}
          >
            <Input placeholder="Nhập mã hồ sơ tác nghiệp" prefix={<FileTextOutlined />} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setBulkQuickModalVisible(false); bulkQuickForm.resetFields(); }}>Hủy</Button>
              <Button type="primary" htmlType="submit" loading={bulkQuickLoading} disabled={!canBulkQuickComplete} icon={<ThunderboltOutlined />}>Hoàn thành nhanh</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CampaignWorkRecordsTable; 